import React, {useContext, useEffect, useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {
    getSettingsInfo,
    editProductSettings,
    getCountry,
    getState,
    getCity,
    getCustomerSettingsInfo, editCustomerSettings
} from '../../api/api';
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import AuthContext from "../../context/AuthContext";
import {Label, Button, Form, FormGroup, FormFeedback} from "reactstrap";
import Select from "react-select";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import Loader from "../../components/Loader";

function CustomerSettingsTab({currentTab, setCurrentTab}) {
    const {refreshToken} = useContext(AuthContext);
    const queryClient = useQueryClient();
    const [defaultCustomerCountry, setDefaultCustomerCountry] = useState('')
    const [defaultCustomerState, setDefaultCustomerState] = useState('')
    const [defaultCustomerCity, setDefaultCustomerCity] = useState('')
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);

    const { status, data, error, refetch } = useQuery({
        queryKey: ['customer-settings'],
        queryFn: () => getCustomerSettingsInfo(),
        keepPreviousData : true,
        retry: false,
        onError: (err => {
            handleUnauthorized401QueryError(err, refreshToken, refetch)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }),
    })


    useQuery(
        ['country'],
        () => getCountry(1, '', '','', '', 100),
        {
            onSuccess: (data) => {
                const options = data.results.map((country) => ({
                    value: country.name, label: country.name }));
                setCountryOptions(options);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            }
        }
    );

    useQuery(
        ['state', defaultCustomerCountry?.value],
        () => getState(1, '', '', defaultCustomerCountry?.value, '', '',
            100),
        {
            onSuccess: (data) => {
                const options = data.results.map((state) => ({
                    value: state.name, label: state.name }));
                setStateOptions(options);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            },
        }
    );

    useQuery(
        ['city', defaultCustomerState?.value],
        () => getCity(1, '', '', defaultCustomerState?.value, '', '', 2000),
        {
            onSuccess: (data) => {
                const options = data.results.map((city) => ({
                    value: city.name, label: city.name,
                }));
                setCityOptions(options);
            },
            onError: (error) => {
                handleUnauthorized401QueryError(error, refreshToken);
                if (error.response.status !== 401) {
                    toast.error(`An error occurred! ${error.response.data.detail}`);
                }
            },
            enabled: !!defaultCustomerState?.value,
        }
    );


    const editCustomerSettingsMutation = useMutation({
        mutationFn: editCustomerSettings,
        onSuccess: (data, variables) =>{
            // toggleCustomerSettingsEditModal()
            queryClient.invalidateQueries(['customer-settings'])
            toast.success('Customer Settings Updated!')
        },

        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, editCustomerSettingsMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const handleEditCustomerSettings = async (setting, e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("default_customer_country", defaultCustomerCountry?.value || '');
        formData.append("default_customer_state", defaultCustomerState?.value || '');
        formData.append("default_customer_city", defaultCustomerCity?.value || '');


        editCustomerSettingsMutation.mutate({
            formData: formData, settingId: setting.id
        }, )
    };

    useEffect(() => {
        if (data && data.results[0]) {
            setDefaultCustomerCountry({
                value: data.results[0].default_customer_country,
                label: data.results[0].default_customer_country,
            });
            setDefaultCustomerState({
                value: data.results[0].default_customer_state,
                label: data.results[0].default_customer_state,
            });
            setDefaultCustomerCity({
                value: data.results[0].default_customer_city,
                label: data.results[0].default_customer_city,
            });
        }
    }, [data])


    return (
        <>
            {status === 'success' | error?.response?.status === 401 && (
                <Form onSubmit={(e) => handleEditCustomerSettings(data.results[0], e)}>
                     <div className="row">
                          <div className="settings-card ml-3">
                               <div className="card-box">
                                   <div className="form-group">
                                        <FormGroup>
                                            <Label for="default_customer_country">Default Customer Country</Label>
                                            <Select
                                                options={countryOptions}
                                                onChange={(option) => {
                                                      setDefaultCustomerCountry(option);
                                                      setDefaultCustomerState(null);
                                                      setDefaultCustomerCity(null);
                                                }}
                                                value={defaultCustomerCountry}
                                                styles={{
                                                     control: (baseStyles) => (
                                                         {
                                                               ...baseStyles,
                                                               borderColor: "#495561",
                                                               backgroundColor: "#414d59",
                                                               color: "white",
                                                               padding: "0.45 0.9",
                                                               border: 'none',
                                                               boxShadow: 'none'
                                                        }
                                                     ),
                                                     menu: (baseStyles) => (
                                                         {
                                                             ...baseStyles,
                                                             backgroundColor: "#414d59",
                                                             color: "#a8b5c3"
                                                         }
                                                    ),
                                                    input: (baseStyles) => (
                                                         {
                                                        ...baseStyles,
                                                        color: "white"
                                                         }
                                                     ),
                                                     singleValue: (baseStyles) => ({
                                                         ...baseStyles,
                                                         color: "white"
                                                     }),
                                                }}
                                                invalid={
                                                     editCustomerSettingsMutation.isError &&
                                                     editCustomerSettingsMutation.error.response.data.default_customer_country
                                                }

                                            />
                                            {editCustomerSettingsMutation.isError && editCustomerSettingsMutation.error.response.data.default_customer_country && (
                                                  <FormFeedback invalid>
                                                      {editCustomerSettingsMutation.error.response.data.default_customer_country}
                                                  </FormFeedback>
                                           )}
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="default_customer_country">
                                                Default Customer State
                                            </Label>
                                            <Select
                                                options={stateOptions}
                                                onChange={(option) => {
                                                    setDefaultCustomerState(option);
                                                    setDefaultCustomerCity(null);
                                                }}
                                                value={defaultCustomerState}
                                                isDisabled={!defaultCustomerCountry}
                                                styles={{
                                                    control: (baseStyles) => (
                                                        {
                                                            ...baseStyles,
                                                            borderColor: "#495561",
                                                            backgroundColor: "#414d59",
                                                            color: "white",
                                                            padding: "0.45 0.9",
                                                            border: 'none',
                                                            boxShadow: 'none'
                                                        }
                                                    ),
                                                    menu: (baseStyles) => (
                                                        {
                                                            ...baseStyles,
                                                            backgroundColor: "#414d59",
                                                            color: "#a8b5c3"
                                                        }
                                                    ),
                                                    input: (baseStyles) => (
                                                        {
                                                            ...baseStyles,
                                                            color: "white"
                                                        }
                                                    ),
                                                    singleValue: (baseStyles) => ({
                                                        ...baseStyles,
                                                        color: "white"
                                                    }),
                                                }}
                                                invalid={
                                                    editCustomerSettingsMutation.isError &&
                                                    editCustomerSettingsMutation.error.response.data.default_customer_state
                                                }
                                            />
                                            {editCustomerSettingsMutation.isError && editCustomerSettingsMutation.error.response.data.default_customer_state && (
                                                <FormFeedback invalid>
                                                    {editCustomerSettingsMutation.error.response.data.default_customer_state}
                                                </FormFeedback>
                                            )}
                                        </FormGroup>
                                        <FormGroup>
                                            <Label for="default_customer_city">
                                                Default Customer City
                                            </Label>
                                            <Select
                                                options={cityOptions}
                                                onChange={(option) => {
                                                    setDefaultCustomerCity(option)
                                                }}
                                                value={defaultCustomerCity}
                                                isDisabled={!defaultCustomerState}
                                                styles={{
                                                    control: (baseStyles) => (
                                                        {
                                                            ...baseStyles,
                                                            borderColor: "#495561",
                                                            backgroundColor: "#414d59",
                                                            color: "white",
                                                            padding: "0.45 0.9",
                                                            border: 'none',
                                                            boxShadow: 'none'
                                                        }
                                                    ),
                                                    menu: (baseStyles) => (
                                                        {
                                                            ...baseStyles,
                                                            backgroundColor: "#414d59",
                                                            color: "#a8b5c3"
                                                        }
                                                    ),
                                                    input: (baseStyles) => (
                                                        {
                                                            ...baseStyles,
                                                            color: "white"
                                                        }
                                                    ),
                                                    singleValue: (baseStyles) => ({
                                                        ...baseStyles,
                                                        color: "white"
                                                    }),
                                                }}
                                                invalid={
                                                    editCustomerSettingsMutation.isError &&
                                                    editCustomerSettingsMutation.error.response.data.default_customer_city
                                                }
                                            />
                                            {editCustomerSettingsMutation.isError && editCustomerSettingsMutation.error.response.data.default_customer_city && (
                                                <FormFeedback invalid>
                                                    {editCustomerSettingsMutation.error.response.data.default_customer_city}
                                                </FormFeedback>
                                            )}
                                        </FormGroup>
                                        <Button type="submit" className="btn bx-btn-primary waves-effect waves-light" disabled={editCustomerSettingsMutation.isLoading}>{editCustomerSettingsMutation.isLoading? <><Loader size={'small'}/>Saving...</>: `Save`}</Button>
                                   </div>
                                </div>
                          </div>
                     </div>
                </Form>
            )}
        </>
    );

}

export default CustomerSettingsTab;